import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth, signInAnonymously } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getMessaging } from "firebase/messaging";
import { compressImage } from '../utils/imageUtils';

/**
 * Deletes a file from Firebase Storage using its download URL.
 * @param url The public download URL of the file.
 */
export async function deleteFileFromStorage(url: string): Promise<void> {
    if (!url) return;
    try {
        await ensureFirebaseAuth();
        // Firebase storage ref() can handle full https URLs
        const fileRef = ref(storage, url);
        await deleteObject(fileRef);
    } catch (error) {
        console.error('[Firebase] Failed to delete file:', error);
    }
}

const firebaseConfig = {
    apiKey: "AIzaSyC0XUQqk51NGLazlnaGKsPAgjkNNbgZR-E",
    authDomain: "markwave-481315.firebaseapp.com",
    projectId: "markwave-481315",
    storageBucket: "markwave-481315.firebasestorage.app",
    messagingSenderId: "612299373064",
    appId: "1:612299373064:web:f3a45cb23c3990060eefbd",
    measurementId: "G-R0G3KPKH1M"
};

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

const db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
});

let messaging: ReturnType<typeof getMessaging> | null = null;
try {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    messaging = getMessaging(app);
  }
} catch (e) {
  console.warn('[Firebase] Messaging not supported in this browser:', e);
}

// ─── Environment detection ────────────────────────────────────────────────────

function getEnvFolder(): 'staging' | 'production' {
    const hostname = window.location.hostname;
    const buildEnv = (process.env.REACT_APP_TAG || process.env.REACT_APP_ENV || '').toLowerCase();
    if (
        buildEnv.includes('staging') || buildEnv.includes('dev') || buildEnv.includes('stagging') ||
        hostname === 'localhost' || hostname === '127.0.0.1' ||
        hostname.includes('dev') || hostname.includes('staging') || hostname.includes('stage')
    ) {
        return 'staging';
    }
    return 'production';
}

// ─── Storage path builders ────────────────────────────────────────────────────
//
// Structure:
//   farmvest/{staging|production}/onboarding/{YYYY-MM-DD}/{mobile}/image_{ts}.jpg
//   farmvest/{staging|production}/cpf_payments/{method}/{YYYY-MM-DD}/{mobile}/image_{ts}.jpg
//   farmvest/{staging|production}/profile/{user_id}/profile.jpg
//
// CPF payment method values:
//   bank_transfer | online_payment | manual_cash | manual_bank_transfer | cheque

export const StoragePaths = {
    onboarding: (env: string, date: string, mobile: string) =>
        `farmvest/${env}/onboarding/${date}/${mobile}`,

    cpfPayment: (env: string, method: string, date: string, mobile: string) =>
        `farmvest/${env}/cpf_payments/${method}/${date}/${mobile}`,

    profile: (env: string, userId: string) =>
        `farmvest/${env}/profile/${userId}`,

    withdrawalProof: (env: string, withdrawalId: number) =>
        `farmvest/${env}/withdrawal_proofs/${withdrawalId}`,
};

// ─── Firebase Auth (anonymous) ────────────────────────────────────────────────
// Storage rules require request.auth != null. The dashboard has no user-facing
// Firebase login, so we sign in anonymously once per session before any upload.

const auth = getAuth(app);

async function ensureFirebaseAuth(): Promise<void> {
    if (auth.currentUser) return;
    try {
        await signInAnonymously(auth);
    } catch (e) {
        console.warn('[Firebase] Anonymous sign-in failed:', e);
    }
}

// ─── Core upload helper ───────────────────────────────────────────────────────

async function uploadFileToStorage(
    file: File,
    folderPath: string,
    onProgress?: (progress: number) => void
): Promise<string> {
    if (onProgress) onProgress(0); // Report start immediately
    await ensureFirebaseAuth();
    let fileToUpload = file;
    if (file.type.startsWith('image/')) {
        try { fileToUpload = await compressImage(file, 0.7, 1280); } catch (_) { }
    }
    const timestamp = Date.now();
    const cleanName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const storagePath = `${folderPath}/image_${timestamp}_${cleanName}`;
    const storageRef = ref(storage, storagePath);

    const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

    return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (onProgress) onProgress(progress);
            },
            (error) => {
                reject(error);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
            }
        );
    });
}

function todayDateFolder(): string {
    return new Date().toISOString().split('T')[0]; // YYYY-MM-DD
}

// ─── Public upload functions ──────────────────────────────────────────────────

/**
 * Upload an animal onboarding image.
 * @param mobile  Investor mobile number used as sub-folder (use 'admin' if not known)
 * @param onProgress  Optional callback for upload progress percentage
 */
export async function uploadOnboardingImage(
    file: File,
    mobile = 'admin',
    onProgress?: (progress: number) => void
): Promise<string> {
    const env = getEnvFolder();
    const folder = StoragePaths.onboarding(env, todayDateFolder(), mobile);
    return uploadFileToStorage(file, folder, onProgress);
}

/**
 * Upload a CPF payment proof image.
 * @param method  Payment method key: bank_transfer | online_payment | manual_cash | manual_bank_transfer | cheque
 * @param mobile  Investor mobile number used as sub-folder (use 'unknown' if not available)
 * @param onProgress  Optional callback for upload progress percentage
 */
export async function uploadCpfPaymentImage(
    file: File,
    method: string,
    mobile = 'unknown',
    onProgress?: (progress: number) => void
): Promise<string> {
    const env = getEnvFolder();
    const folder = StoragePaths.cpfPayment(env, method, todayDateFolder(), mobile);
    return uploadFileToStorage(file, folder, onProgress);
}

/**
 * Upload an admin payout receipt image (used in Wallet payout modal).
 */
export async function uploadWithdrawalProofImage(
    file: File,
    withdrawalId: number,
    onProgress?: (progress: number) => void
): Promise<string> {
    const env = getEnvFolder();
    const folder = StoragePaths.withdrawalProof(env, withdrawalId);
    return uploadFileToStorage(file, folder, onProgress);
}

/**
 * Legacy wrapper — used by AnimalOnboarding and UnallocatedAnimals components.
 * Routes to the new onboarding path so all uploads are consistently structured.
 */
export async function uploadToFirebase(
    file: File,
    mobile = 'admin',
    onProgress?: (progress: number) => void
): Promise<string> {
    return uploadOnboardingImage(file, mobile, onProgress);
}

/**
 * Upload a user profile image.
 * @param file The image file to upload
 * @param identifier The mobile number or temp identifier
 * @param onProgress Optional callback for progress
 */
export async function uploadProfileImage(
    file: File,
    identifier = 'temp',
    onProgress?: (progress: number) => void
): Promise<string> {
    const env = getEnvFolder();
    const folder = `farmvest/${env}/profile/${identifier}`;
    return uploadFileToStorage(file, folder, onProgress);
}


export { storage, db, messaging };
